import { useEffect, useId, useState, type FormEvent } from "react";
import { Button, ErrorState, Spinner } from "@/design";
import { ApiError, type PasskeyChallenge } from "@/lib/api";
import { CodeInput } from "./CodeInput";
import { BackButton, StepHeading } from "./OnboardingFrame";
import type { SignupDraft } from "./SignupFlow";

const RESEND_AFTER = 30;
/** Wrong code (401) and expired or over-tried code (400), as the API reports them. */
const BAD_CODE = new Set(["passkey_verification_failed", "challenge_expired"]);

export function CodeStep({
  draft,
  update,
  resend: sendAgain,
  verify,
  onVerified,
  onChangeEmail,
}: {
  draft: SignupDraft;
  update: (p: Partial<SignupDraft>) => void;
  /** Repeats the sign-up request; resolves to the new verification id. */
  resend: () => Promise<string>;
  verify: (code: string) => Promise<PasskeyChallenge>;
  onVerified: () => void;
  onChangeEmail: () => void;
}) {
  const [code, setCode] = useState(draft.code);
  const [checking, setChecking] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [wait, setWait] = useState(RESEND_AFTER);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const hintId = useId();

  useEffect(() => {
    if (wait <= 0) return;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  async function check(value: string) {
    if (value.length !== 6 || checking) return;
    setChecking(true);
    setCodeError(null);
    setError(null);
    try {
      await verify(value);
      onVerified();
    } catch (e) {
      if (e instanceof ApiError && BAD_CODE.has(e.code)) {
        setCodeError(e.code === "challenge_expired" ? "That code has expired, or has been tried too often. Send a new one below." : "That code isn't right. Check the newest email from Guv and try again.");
        setCode("");
      } else {
        setError(e);
      }
    } finally {
      setChecking(false);
    }
  }

  async function resend() {
    setResending(true);
    setError(null);
    setCodeError(null);
    try {
      update({ verificationId: await sendAgain(), code: "" });
      setCode("");
      setResent(true);
      setWait(RESEND_AFTER);
    } catch (e) {
      setError(e);
    } finally {
      setResending(false);
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void check(code);
  };

  return (
    <form onSubmit={submit} noValidate>
      <BackButton onClick={onChangeEmail} />
      <StepHeading title="Check your email">
        We sent a 6-digit code to <span className="break-all font-medium text-text">{draft.email}</span>. It works for 10 minutes.
      </StepHeading>

      <div className="mt-8">
        <CodeInput
          value={code}
          onChange={(v) => {
            setCode(v);
            if (codeError) setCodeError(null);
          }}
          onComplete={(v) => void check(v)}
          disabled={checking}
          invalid={!!codeError}
          autoFocus
          describedBy={hintId}
        />
        <p id={hintId} className={codeError ? "mt-2.5 text-sm text-danger-text" : "mt-2.5 text-sm text-muted"} aria-live="polite">
          {checking ? (
            <span className="inline-flex items-center gap-2">
              <Spinner className="size-3.5" /> Checking your code…
            </span>
          ) : (
            codeError ?? "Paste it straight in, or type it. We check it as soon as all six digits are in."
          )}
        </p>
      </div>

      <Button type="submit" variant="primary" size="lg" className="mt-6 w-full" loading={checking} disabled={code.length !== 6}>
        Continue
      </Button>
      {error != null && <ErrorState error={error} className="mt-5" />}

      <div className="mt-8 space-y-2 border-t border-border pt-5 text-sm text-muted">
        <p>
          Nothing there? Check your spam folder for an email from Guv. If you already have an account, we'll have emailed you a sign-in link instead.{" "}
          {wait > 0 ? (
            <span className="tabular-nums text-subtle">You can ask for a new code in {wait}s.</span>
          ) : (
            <button type="button" onClick={() => void resend()} disabled={resending} className="rounded-sm font-medium text-accent-text underline-offset-4 hover:underline disabled:opacity-60">
              {resending ? "Sending…" : "Send a new code"}
            </button>
          )}
        </p>
        {resent && wait > 0 && (
          <p className="text-ok-text" role="status">
            New code sent. Only the newest code works.
          </p>
        )}
        <p>
          Wrong address?{" "}
          <button type="button" onClick={onChangeEmail} className="rounded-sm font-medium text-accent-text underline-offset-4 hover:underline">
            Use a different email
          </button>
        </p>
      </div>
    </form>
  );
}
